import { useEffect, useRef } from 'react'
import { useOnboardingStoreV2 } from '@forwardflow/store'
import {
   useGetFubStages,
   useGetPeopleTags,
} from '@forwardflow/queries/onboarding.queries'
import {
   useFubPeopleSources,
   useFubPonds,
} from '@forwardflow/queries/fub.queries'
import LocalStorage from '@forwardflow/services/LocalStorage'
import { TOnboardingStep } from '@forwardflow/types/onboarding'

// Steps that require refetching FUB data
const STEPS_REQUIRING_REFETCH: TOnboardingStep[] = [
   'stage-mapping-nurture-stages',
   'stage-mapping-appointment',
   'stage-mapping-stop-stages',
   'campaign-configuration',
]

/**
 * Custom hook to handle all onboarding data loading and synchronization.
 * Extracts data fetching logic from the layout component to maintain separation of concerns.
 *
 * @param email - User's email address
 * @returns Object containing loading state
 */
export const useOnboardingDataLoader = (email: string | undefined) => {
   const {
      initializeOnboardingData,
      setEmail,
      isLoadingOrInitialization,
      setIsLoadingOrInitialization,
      setStages,
      setTags,
      setSources,
      setPonds,
      onboardingData,
   } = useOnboardingStoreV2()

   // Determine organization ID (handle bypass emails for testing)
   const organizationId = LocalStorage.get('currentOrganization')

   // Fetch FUB stages with mutate function for manual refetch
   const { data: fubStages, mutate: refetchFubStages } = useGetFubStages(
      onboardingData?.organizationId || '',
      10000,
      1,
      onboardingData?.isFUBConnected
   )

   // Fetch people tags with mutate function for manual refetch
   const { data: peopleTags, mutate: refetchPeopleTags } = useGetPeopleTags(
      onboardingData?.organizationId || organizationId || '',
      1,
      10000,
      Boolean(onboardingData?.isFUBConnected)
   )

   // Fetch FUB ponds with mutate function for manual refetch
   const { data: pondsData, mutate: refetchPonds } = useFubPonds(
      onboardingData?.organizationId || '',
      1,
      10000,
      Boolean(onboardingData?.isFUBConnected)
   )

   // Fetch FUB people sources with mutate function for manual refetch
   const { data: sourcesData, mutate: refetchSources } = useFubPeopleSources(
      onboardingData?.organizationId || '',
      1,
      10000,
      Boolean(onboardingData?.isFUBConnected)
   )

   // Sync people tags to store
   useEffect(() => {
      if (peopleTags) {
         setTags(peopleTags?.data?.map((item) => item?.tagName || '') || [])
      }
   }, [peopleTags, setTags])

   // Sync FUB stages to store
   useEffect(() => {
      if (fubStages) {
         setStages(
            (fubStages?.data || [])?.map((item: any) => ({
               id: item?.stageId,
               name: item?.stageName,
               description: '',
            })) || []
         )
      }
   }, [fubStages, setStages])

   // Sync ponds to store
   useEffect(() => {
      if (pondsData) {
         setPonds(pondsData?.map((item) => item?.pondName || '') || [])
      }
   }, [pondsData, setPonds])

   // Sync sources to store
   useEffect(() => {
      if (sourcesData) {
         setSources(sourcesData?.map((item) => item?.leadSource || '') || [])
      }
   }, [sourcesData, setSources])

   // Sync email to store
   useEffect(() => {
      if (email) {
         setEmail(email)
      }
   }, [setEmail, email])

   // Initialize onboarding data when organization is available
   useEffect(() => {
      if (organizationId) {
         initializeOnboardingData(organizationId)
         setIsLoadingOrInitialization(false)
      } else {
         setIsLoadingOrInitialization(false)
      }
   }, [organizationId, initializeOnboardingData, setIsLoadingOrInitialization])

   // Track previous step to detect changes
   const prevStepRef = useRef(onboardingData?.currentStep)

   // Refetch data when onboarding step changes to specific steps
   useEffect(() => {
      const currentStep = onboardingData?.currentStep

      // Only refetch if step actually changed (not initial render) and is one of the required steps
      if (
         prevStepRef.current &&
         prevStepRef.current !== currentStep &&
         currentStep &&
         STEPS_REQUIRING_REFETCH.includes(currentStep)
      ) {
         // Refetch all FUB-related data when step changes to required steps
         if (onboardingData?.isFUBConnected) {
            refetchFubStages()
            refetchPeopleTags()
            refetchPonds()
            refetchSources()
         }
      }

      // Update the previous step reference
      prevStepRef.current = currentStep
   }, [
      onboardingData?.currentStep,
      onboardingData?.isFUBConnected,
      refetchFubStages,
      refetchPeopleTags,
      refetchPonds,
      refetchSources,
   ])

   return {
      isLoading: isLoadingOrInitialization,
      organizationId,
   }
}
